import type { Channel, ChannelFilters, ChannelSort } from 'stream-chat';
import { isFollowing } from './follow';

/**
 * TODO: SE-2592 cleanup — exported only for the legacy web-app screens (`useDirectoryChannels`, the
 * web channels page) and `getChannelsFilters`, which pair it with hand-built filters. While it is
 * public, {@link buildChannelQuery}'s filter/sort pairing can still be bypassed by a caller that takes
 * filters from one place and this sort from another. Unexport it with those callers.
 */
export const channelsSort: ChannelSort = [{ pinned_at: -1 }, { last_message_at: -1 }, { updated_at: -1 }];

/**
 * Sort for lists that surface channels the user is not a member of.
 *
 * `pinned_at` lives on the membership, so sorting by it silently restricts the query to joined
 * channels. Dropping it costs pinned-first ordering but returns lurked channels at all.
 */
const lurkableChannelsSort: ChannelSort = [{ last_message_at: -1 }, { updated_at: -1 }];

/**
 * Builds channel filters for Stream Chat queries scoped to a specific project.
 *
 * Returns channels the user has joined, filtered by project ID. Includes:
 * - Group, team, and issue channels
 * - Direct messaging channels (only if they have messages)
 * - Personal channels
 *
 * When a search term is provided, filters are applied to match against
 * channel name or member user name using autocomplete.
 *
 * TODO: SE-2592 cleanup — this duplicates `buildChannelQuery({ list: 'all' })`. When the
 * `channel-following` flag is removed and `LegacyChannelListScreen` is deleted, migrate the remaining
 * web-app callers to `buildChannelQuery` and remove this function.
 *
 * @param projectId - The project ID to scope channels to
 * @param searchChannelText - Optional search term for channel/member name autocomplete
 * @returns Combined channel filters for the Stream Chat query
 */
export const getChannelsFilters = (projectId: string, searchChannelText?: string): ChannelFilters => {
  const searchFilters: ChannelFilters = searchChannelText
    ? {
        $or: [
          { name: { $autocomplete: searchChannelText } },
          { 'member.user.name': { $autocomplete: searchChannelText } },
        ],
      }
    : {};

  return {
    $or: [
      {
        type: { $in: ['group', 'team', 'issue', 'personal'] },
        joined: true,
        shape_project_id: projectId,
        ...searchFilters,
      },
      {
        type: 'messaging',
        joined: true,
        last_message_at: { $exists: true },
        shape_project_id: projectId,
        ...searchFilters,
      },
    ],
  };
};

/**
 * Filters a list of channels to include only those belonging to the specified project.
 *
 * Channels without a `shape_project_id` are included (as they are not project-scoped).
 * Channels with a `shape_project_id` that does not match the given project are excluded.
 *
 * @param channels - Array of Stream Chat channels to filter
 * @param projectId - The project ID to filter channels by
 * @returns Filtered array of channels belonging to the specified project
 */
export const filterChannelsList = (channels: Array<Channel>, projectId: string) => {
  return channels.filter((channel) => {
    return channel.data?.shape_project_id === projectId;
  });
};

/**
 * Filters a list of channels down to issue channels for the specified project.
 *
 * Used as the Issues tab's `channelRenderFilterFn`. The server-side query already scopes to
 * `type: 'issue'`, but Stream's ChannelList promotes channels into the mounted list on real-time
 * events (e.g. a new direct message) without re-checking the query filters, so a non-issue channel
 * can leak into the tab. Restricting to `type === 'issue'` here keeps those promoted channels out.
 *
 * @param channels - Array of Stream Chat channels to filter
 * @param projectId - The project ID to filter channels by
 * @returns Filtered array of issue channels belonging to the specified project
 */
export const filterIssueChannelsList = (channels: Array<Channel>, projectId: string) => {
  return filterChannelsList(channels, projectId).filter((channel) => channel.type === 'issue');
};

/**
 * Filters a list of channels down to followed channels for the specified project.
 *
 * Used as the Following tab's `channelRenderFilterFn`. The server-side query already filters
 * `archived: false` (followed only), but Stream's ChannelList promotes channels into the mounted list
 * on real-time events (e.g. a new message) without re-checking the query filters, so an unfollowed
 * (archived) channel that receives a message can leak into the tab. Re-checking `isFollowing` here
 * keeps those promoted channels out.
 *
 * @param channels - Array of Stream Chat channels to filter
 * @param projectId - The project ID to filter channels by
 * @returns Filtered array of followed channels belonging to the specified project
 */
export const filterFollowingChannelsList = (channels: Array<Channel>, projectId: string) => {
  return filterChannelsList(channels, projectId).filter((channel) => isFollowing(channel));
};

/**
 * Filters a list of channels down to location channels for the specified project.
 *
 * Used as the Locations tab's `channelRenderFilterFn`. The server-side query already scopes to
 * `type: 'location'`, but Stream's ChannelList promotes channels into the mounted list on real-time
 * events (e.g. a new message in a joined channel) without re-checking the query filters, so a
 * non-location channel can leak into the tab. Restricting to `type === 'location'` here keeps those
 * promoted channels out.
 *
 * @param channels - Array of Stream Chat channels to filter
 * @param projectId - The project ID to filter channels by
 * @returns Filtered array of location channels belonging to the specified project
 */
export const filterLocationChannelsList = (channels: Array<Channel>, projectId: string) => {
  return filterChannelsList(channels, projectId).filter((channel) => channel.type === 'location');
};

/**
 * The filter-chip lists on the channel list screen. Each maps to one chip:
 * - `all` — every joined channel in the project (followed or not), plus every location channel when the
 *   location-channels feature is enabled.
 * - `following` — the same joined set, followed only (`archived: false`), plus followed location channels
 *   when the location-channels feature is enabled.
 * - `issues` — joined issue channels, followed or not.
 * - `locations` — every location channel in the project, joined or not (lurk access).
 */
export type ChannelListName = 'all' | 'following' | 'issues' | 'locations';

export type BuildChannelQueryOptions = {
  list: ChannelListName;
  projectId: string;
  /** Optional autocomplete term matched against channel name and member user name. */
  search?: string;
  /**
   * Whether the location-channels feature is on. When `false` (the default), `all` and `following`
   * omit location channels entirely, so nothing leaks into those lists while the feature is gated.
   * The `locations` list is unaffected — it is only ever queried from the feature's own tab.
   *
   * TODO: SE-2933 cleanup — when the `location-channels` flag is removed, drop this option and inline
   * the enabled branches here and in `buildChannelSort`. The flag is also read by `ProjectChannelList`,
   * `ChannelScreen` and the channel-list tabs screen, and threaded through `useFollowingUnread`.
   */
  locationChannelsEnabled?: boolean;
};

/**
 * Autocomplete search clause matched against channel name and member user name. Returns an empty
 * object when no term is given, so spreading it adds no `$or`/search key.
 */
const buildSearchFilters = (searchChannelText?: string): ChannelFilters =>
  searchChannelText
    ? {
        $or: [
          { name: { $autocomplete: searchChannelText } },
          { 'member.user.name': { $autocomplete: searchChannelText } },
        ],
      }
    : {};

/**
 * Builds the Stream `ChannelFilters` for a filter-chip list, scoped to one project.
 *
 * Name the list and get the exact server query — callers do not handle the archive→follow mapping,
 * the messaging `last_message_at` branch, or where the search clause is spliced.
 *
 * Follow state lives in Stream's per-member `archived` flag (see `useChannelFollow` for the write and
 * `isFollowing` in `follow.ts` for the canonical read), which maps to the query as:
 * - `following` → `archived: false` (followed only); when `locationChannelsEnabled`, followed location
 *   channels join this set through a `joined` branch, since their follow state works like every other
 *   channel type.
 * - `all` / `issues` → no `archived` clause; Stream returns archived channels by default, so both
 *   followed and unfollowed come back without anything extra. When `locationChannelsEnabled`, `all`
 *   additionally includes location channels, which are lurk-readable (no `joined` clause).
 *
 * Module-private for the same reason as {@link buildChannelSort}: filter and sort are only valid as a
 * pair, so {@link buildChannelQuery} is the single way to obtain either.
 *
 * @returns The `ChannelFilters` for the given list.
 */
const buildChannelFilters = ({
  list,
  projectId,
  search,
  locationChannelsEnabled = false,
}: BuildChannelQueryOptions): ChannelFilters => {
  const searchFilters = buildSearchFilters(search);

  const joinedBranch: ChannelFilters = {
    type: { $in: ['group', 'team', 'issue', 'personal'] },
    joined: true,
    shape_project_id: projectId,
    ...searchFilters,
  };

  const messagingBranch: ChannelFilters = {
    type: 'messaging',
    joined: true,
    last_message_at: { $exists: true },
    shape_project_id: projectId,
    ...searchFilters,
  };

  // Lurk-readable: every project user sees location channels whether or not they have joined, so
  // there is no `joined` clause. No `archived` clause either — follow state does not scope them.
  const locationBranch: ChannelFilters = {
    type: 'location',
    shape_project_id: projectId,
    ...searchFilters,
  };

  if (list === 'issues') {
    return {
      type: 'issue',
      joined: true,
      shape_project_id: projectId,
      ...searchFilters,
    };
  }

  if (list === 'locations') return locationBranch;

  if (list === 'following') {
    // Followed location channels behave like any other followed channel — a joined member that is not
    // archived — so they scope through the shared `archived: false` clause with a `joined` location
    // branch. (`all` instead surfaces every location channel lurk-readable, without a `joined` clause.)
    return {
      archived: false,
      $or: locationChannelsEnabled
        ? [joinedBranch, messagingBranch, { ...locationBranch, joined: true }]
        : [joinedBranch, messagingBranch],
    };
  }

  // `all` also surfaces location channels, lurk-readable, alongside the member's own joined channels.
  return {
    $or: locationChannelsEnabled ? [joinedBranch, messagingBranch, locationBranch] : [joinedBranch, messagingBranch],
  };
};

/**
 * Render-time `channelRenderFilterFn` for each filter-chip list, keyed by {@link ChannelListName}.
 *
 * Stream's `<ChannelList>` promotes channels into the mounted list on real-time events without
 * re-checking the server query (see the individual `filter*ChannelsList` functions), so each list
 * re-applies its own scope client-side. Keying by list name keeps the dispatch exhaustive: a new
 * `ChannelListName` is a compile error here until its render filter is registered.
 */
export const channelListRenderFilters: Record<
  ChannelListName,
  (channels: Array<Channel>, projectId: string) => Array<Channel>
> = {
  all: filterChannelsList,
  issues: filterIssueChannelsList,
  following: filterFollowingChannelsList,
  locations: filterLocationChannelsList,
};

type BuildChannelSortOptions = Omit<BuildChannelQueryOptions, 'projectId' | 'search'>;

/**
 * Builds the Stream query sort for a filter-chip list.
 *
 * Lists whose filter admits unjoined channels must sort with {@link lurkableChannelsSort}: `locations`
 * always, `all` only while the flag is on (that is when it gains a location branch with no `joined`
 * clause). `following` and `issues` stay joined-only, so they keep pinned-first ordering.
 *
 * The sort is only correct for the filter built from the same options, so callers go through
 * {@link buildChannelQuery} rather than pairing the two by hand.
 */
const buildChannelSort = ({ list, locationChannelsEnabled = false }: BuildChannelSortOptions): ChannelSort => {
  if (list === 'locations') return lurkableChannelsSort;

  if (list === 'all' && locationChannelsEnabled) return lurkableChannelsSort;

  return channelsSort;
};

export type ChannelQuery = {
  filters: ChannelFilters;
  sort: ChannelSort;
};

/**
 * Builds the complete Stream query — filter and sort — for a filter-chip list.
 *
 * The two are not independent: whether the filter admits unjoined channels decides whether the sort
 * may use `pinned_at` (see {@link lurkableChannelsSort}). Deriving both from one set of options is
 * what keeps them consistent — both halves are module-private, so no caller can take one without the
 * other and let the pair drift.
 */
export const buildChannelQuery = (options: BuildChannelQueryOptions): ChannelQuery => ({
  filters: buildChannelFilters(options),
  sort: buildChannelSort(options),
});
